import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useRouter } from "next/router";
import getGenerateToken from "@helpers/getGenerateToken";
import { store } from "@redux/store";
import { ONLYLETTER } from "../../../../utils/regex";
import { TChangePassword, TResp } from "../type";
import { setOpenSnackbar } from "@redux/slices/rootSnackbar";
import { FORGOT_PASSWORD_SERVICES } from "@repositories/forgot-password/forgotPassword.hooks";
import { getOTPData } from "@helpers/storage";
import { PROFILE_API } from "@repositories/profile/profile.hooks";
import useResponsive from "@helpers/useResponsive";
import { i18n } from "next-i18next";
import { useLanguage } from "@utils/useLanguage";

const useAction = (
  setCloseDialog: Dispatch<SetStateAction<boolean>> | undefined,
  resetType: string,
) => {
  const initialValueForm: Partial<TChangePassword> = {
    newPassword: "",
    reEnterNewPassword: "",
  };
  const { language } = useLanguage();
  const smClient = useResponsive("sm");
  const router = useRouter();
  const OTPData = getOTPData();

  const [visiblePass, setVisiblePass] = useState<boolean>(false);
  const [visibleRePass, setVisibleRePass] = useState<boolean>(false);
  const [isLoad, setIsLoad] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isInputPass, setIsInputPass] = useState(true);
  const [verifyPass, setVerifyPass] = useState({
    isMin8: false,
    isAlpha: false,
    isNum: false,
  });

  const [reVerifyPass, setReVerifyPass] = useState({
    isReMin8: false,
    isReAlpha: false,
    isReNum: false,
  });

  const callApiResetPassword = async (value: Partial<TChangePassword>) => {
    return FORGOT_PASSWORD_SERVICES.PUT_RESET_PASSWORD(
      {
        ...value,
        identifier: OTPData?.identifier,
        otp: OTPData?.otp,
        signatureCode: OTPData?.signatureCode,
      },
      await getGenerateToken(),
      language,
    );
  };

  const onSubmit = (value: Partial<TChangePassword>) => {
    setIsLoad(true);
    const apiCall =
      resetType === "reset_password"
        ? callApiResetPassword(value)
        : PROFILE_API.PUT_CUSTOMER_CHANGE_PASSWORD(
          {
            ...value,
            otp: OTPData?.otp,
            signatureCode: OTPData?.signatureCode,
          },
          language,
        );
    apiCall
      .then(({message}) => {
        store.dispatch(
          setOpenSnackbar({
            message: message,
            anchor: smClient ? "bottom-center" : "top-center",
            variant: "success",
          }),
        );
        if (resetType === "change_password" && setCloseDialog) {
          setCloseDialog(false);
        } else {
          router.push("/login");
        }
        setIsLoad(false);
      })
      .catch((err: TResp) => {
        setErrorMessage(err?.details?.message || err?.message);
        setIsLoad(false);
      });
  };

  const validConfig = (charsInput: string) => {
    const checkLetters: boolean = ONLYLETTER.test(charsInput);
    const checkNumeric: boolean = /\d/.test(charsInput);
    setVerifyPass({
      ...verifyPass,
      isMin8: charsInput.length > 7 ? true : false,
      isAlpha: checkLetters,
      isNum: checkNumeric,
    });
  };

  const reValidConfig = (charsInput: string) => {
    const checkLetters: boolean = ONLYLETTER.test(charsInput);
    const checkNumeric: boolean = /\d/.test(charsInput);
    setReVerifyPass({
      ...reVerifyPass,
      isReMin8: charsInput.length > 7 ? true : false,
      isReAlpha: checkLetters,
      isReNum: checkNumeric,
    });
  };

  const onNewPasswordValidate = (event: string) => {
    setErrorMessage("");
    setIsInputPass(true);
    validConfig(event);
  };

  const onReEnterPasswordValidate = (event: string) => {
    setErrorMessage("");
    setIsInputPass(false);
    reValidConfig(event);
  };

  const handleClickPass = (event: boolean) => {
    if (event) {
      setVisiblePass(!visiblePass);
    } else {
      setVisibleRePass(!visibleRePass);
    }
  };

  useEffect(() => {
    if (language) {
      i18n?.changeLanguage(language);
    }
  }, [language]);

  return {
    onSubmit,
    initialValueForm,
    onNewPasswordValidate,
    onReEnterPasswordValidate,
    handleClickPass,
    visiblePass,
    visibleRePass,
    verifyPass,
    validConfig,
    reValidConfig,
    errorMessage,
    isLoad,
    reVerifyPass,
    isInputPass,
    language,
  };
};

export default useAction;
